/**
 * Session storage under this prefix belongs to whoever is signed in. Everything that ends or
 * changes who acts clears it first: sign-out, a sign-out in another tab, forgetting the sign-in, a
 * session change another tab announces, and starting or stopping an impersonation.
 */
const prefix = 'tms.signedIn.'

/** Keys from before the prefix, deleted unread as the app starts. */
const retiredKeys: readonly string[] = ['tms.entitlements.redeem.pendingCode']

/** A session storage key that sign-out clears. */
export function signedInStorageKey(name: string): string {
  return `${prefix}${name}`
}

// Counts the clears, so a writer that began before one knows its person is no longer the one acting.
let generation = 0

/**
 * Which clear the signed-in storage is on. A long-lived writer notes it as it starts and writes
 * nothing once it has moved on: what it holds belongs to someone no longer acting.
 */
export function signedInGeneration(): number {
  return generation
}

/** Clears every key {@link signedInStorageKey} made. Storage errors are swallowed: they must not block sign-out. */
export function clearSignedInStorage(): void {
  generation += 1
  try {
    const keys = Array.from({ length: window.sessionStorage.length }, (_, index) => window.sessionStorage.key(index))
    for (const key of keys) {
      if (key?.startsWith(prefix) === true) {
        window.sessionStorage.removeItem(key)
      }
    }
  } catch {
    // Storage unavailable: nothing was kept.
  }
}

/** Deletes the keys signed-in storage had before its prefix, so none is left behind or read. */
export function dropRetiredStorage(): void {
  try {
    for (const key of retiredKeys) {
      window.sessionStorage.removeItem(key)
    }
  } catch {
    // Storage unavailable: nothing was kept.
  }
}
